import { ReturnComponent } from '@/utils/reactTypes';
import { Box, CircularProgress } from '@mui/material';
import { useEffect, useState } from 'react';

import { ErrorFallback } from './errorBoundaries';

// Ordinary suspends at the root resolve in well under this, so they never paint a
//  fallback at all and navigation stays flicker-free.
const MILLISECONDS_BEFORE_SPINNER_DISPLAY = 300;

// A suspend still unresolved this long after it started is not going to resolve:
//  a query whose request never settles keeps its consumers suspended for as long as
//  the tab stays open. Long enough that a slow-but-working load is never mistaken
//  for a stalled one.
const MILLISECONDS_BEFORE_STALLED_DISPLAY = 60_000;

// Larger than the spinners that sit inside a page section, because this one stands in
// for the entire page.
const SPINNER_DIAMETER = 64;

type FallbackStage = 'blank' | 'spinner' | 'stalled';

// Fallback for the suspense boundaries at the root of the app, where there is no page
//  left to show and an unresolved suspend is otherwise indistinguishable from a dead
//  tab. Escalates from nothing, to a spinner, to a screen the user can act on.
export const RootSuspenseFallback = (): ReturnComponent => {
    const [stage, setStage] = useState<FallbackStage>('blank');

    useEffect(() => {
        const spinnerTimeout = setTimeout(
            () => setStage('spinner'),
            MILLISECONDS_BEFORE_SPINNER_DISPLAY,
        );
        const stalledTimeout = setTimeout(
            () => setStage('stalled'),
            MILLISECONDS_BEFORE_STALLED_DISPLAY,
        );

        return () => {
            clearTimeout(spinnerTimeout);
            clearTimeout(stalledTimeout);
        };
    }, []);

    if (stage === 'stalled') {
        return <ErrorFallback />;
    }

    if (stage === 'spinner') {
        return (
            // Centred in the app root, whose height the app shell gives a definite value.
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '100%',
                }}
            >
                <CircularProgress size={SPINNER_DIAMETER} />
            </Box>
        );
    }

    return null;
};
